//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Instance } from 'tippy.js';

import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { VariantImagePreview } from '@campaigns/components/shared/product-details-modal/drawer/variants/VariantImagePreview';
import { VariantImageUploadPopover } from '@campaigns/components/shared/product-details-modal/drawer/variants/image-preview-popover/VariantImageUploadPopover';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  getVariationsValidationRules,
  isProductImageUrlValid,
  validateQuantity,
} from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { validateProductIdentifier } from '@campaigns/utils/sampling/productIdentifierUtils';
import {
  DefaultIconButton,
  InvisibleButton,
  PrimaryLinkButton,
  TertiaryButton,
} from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyText,
  CaptionErrorText,
  SubsectionText,
} from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import Popover from '@components/molecules/popover/Popover';
import { useUniqueId } from '@hooks/useUniqueId';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { getRevenueSymbolFromFormat } from '@utils/readableNumber';

interface VariantRowProps {
  index: number;
  isExpanded: boolean;
  isViewOnly: boolean;
  onRemove: () => void;
  onToggleExpansion: () => void;
  totalVariations: number;
}

export const VariantRow = ({
  index,
  isExpanded,
  isViewOnly,
  onRemove,
  onToggleExpansion,
  totalVariations,
}: VariantRowProps) => {
  const { samplingCampaignRequest } = useSamplingCampaign();
  const { clearErrors, control, formState, getValues, setValue } =
    useFormContext<EditableBVSamplingProduct>();
  const {
    handleVariantImageSelect,
    isImageForAllVariants,
    setIsImageForAllVariants,
  } = useProductModal();
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const popoverInstanceRef = useRef<Instance | null>(null);

  const fulfillmentType =
    samplingCampaignRequest?.fulfillmentOptions?.fulfillmentType;
  const whoShipsProduct =
    samplingCampaignRequest?.fulfillmentOptions?.whoShipsProduct;
  const isBVShipped =
    fulfillmentType === 'shipped' && whoShipsProduct === 'bazaarvoice';

  const currency = samplingCampaignRequest?.currency ?? 'USD';
  const currencySymbol = getRevenueSymbolFromFormat(currency, false);
  const currencyInstructions =
    currencySymbol && currencySymbol !== currency
      ? `${currency} (${currencySymbol})`
      : currency;

  const field = `variations.${index}.imgUrl` as const;
  const rules = getVariationsValidationRules(index, totalVariations);

  const initialImgUrl = getValues(field);
  const [showPreview, setShowPreview] = useState(!!initialImgUrl);

  // Watched so externally-driven updates (e.g. "apply image to all variants") are reflected
  const watchedImgUrl = useWatch({ control, name: field });
  const mainImgUrl = useWatch({ control, name: 'imgUrl' });
  const isBlobImage = !!watchedImgUrl?.startsWith('blob:');

  // "Apply to all variants" copies the already-confirmed main image URL into every
  // variation directly via setValue, bypassing this row's own Add/upload confirmation flow
  const isAppliedToAllVariants =
    isImageForAllVariants &&
    isTruthy(watchedImgUrl) &&
    watchedImgUrl === mainImgUrl;

  const errors = formState.errors;
  const imageError = errors.variations?.[index]?.imgUrl?.message;

  const hasImage =
    isTruthy(watchedImgUrl) &&
    // Blob URLs only ever come from uploads (this row's own, or applied to all variants),
    // never from typing, so they're safe to preview without the explicit showPreview gate
    (isBlobImage ||
      isAppliedToAllVariants ||
      (showPreview && isProductImageUrlValid(watchedImgUrl)));

  // Watch the GTIN value for this specific variation
  const gtinValue = useWatch({
    control,
    name: `variations.${index}.logisticsRef`,
  });
  const variantName = useWatch({
    control,
    name: `variations.${index}.variantName`,
  });

  // Compute validation state based on the watched value
  const isGtinValid = gtinValue
    ? validateProductIdentifier(gtinValue).isValid
    : false;

  const handleImageSubmit = (file: File | undefined) => {
    if (file) {
      const previousImgUrl = getValues(field);
      const blobUrl = URL.createObjectURL(file);
      setValue(field, blobUrl, { shouldValidate: false });
      clearErrors(field);
      setShowPreview(true);
      revokeBlobUrlIfUnused(previousImgUrl);
    }
    handleVariantImageSelect(index, file);
    setIsImageForAllVariants(false);
    popoverInstanceRef.current?.hide();
  };

  const revokeBlobUrlIfUnused = (imgUrl: string | undefined) => {
    if (!imgUrl?.startsWith('blob:')) {
      return;
    }

    const allValues = getValues();
    const isUsedByMainImage = allValues.imgUrl === imgUrl;

    // Check if any other variant is using this blob URL
    const isUsedByOtherVariants = allValues.variations.some(
      (variation, idx) => idx !== index && variation.imgUrl === imgUrl,
    );

    // Only revoke if this is the only place using the blob URL
    if (!isUsedByMainImage && !isUsedByOtherVariants) {
      URL.revokeObjectURL(imgUrl);
    }
  };

  const handleImageRemove = () => {
    const currentImgUrl = getValues(field);

    // Only revoke the blob URL if it's not being used by the main image or other variants
    revokeBlobUrlIfUnused(currentImgUrl);

    clearErrors(field);
    setValue(field, '', {
      shouldValidate: false,
    });

    setShowPreview(false);
    handleVariantImageSelect(index, undefined);
    setIsImageForAllVariants(false);
  };

  const handlePreview = () => {
    const currentUrl = getValues(field);
    if (!isDefined(currentUrl)) {
      return;
    }
    if (isProductImageUrlValid(currentUrl)) {
      setShowPreview(true);
      setIsImageForAllVariants(false);
    }
  };

  return (
    <Container>
      <VariantAccordion
        content={
          <FieldsContainer>
            <ImageField>
              <FieldLabel>Image</FieldLabel>
              {hasImage && (
                <VariantImagePreview
                  alt='Product preview'
                  isViewOnly={isViewOnly}
                  onRemove={handleImageRemove}
                  src={watchedImgUrl}
                />
              )}
              {!isViewOnly && (
                <Popover
                  connectPopover={(instance) => {
                    popoverInstanceRef.current = instance;
                  }}
                  content={
                    <VariantImageUploadPopover
                      index={index}
                      onClearImage={handleImageRemove}
                      onClosePopover={() => popoverInstanceRef.current?.hide()}
                      onFileSelect={handleImageSubmit}
                      onPreview={handlePreview}
                      onRevokePreviousImage={revokeBlobUrlIfUnused}
                      rules={rules[`variations.${index}.imgUrl`]}
                    />
                  }
                  onTogglePopover={(isOpen) => setIsImagePickerOpen(isOpen)}
                  placement='left'
                  shouldOpenOnHover={false}
                  target={
                    hasImage ? (
                      <ChangeImageLinkButton
                        aria-expanded={isImagePickerOpen}
                        aria-invalid={!!imageError}
                        trackingId={
                          SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_VARIATION_IMAGE_POPOVER
                        }
                      >
                        Change
                      </ChangeImageLinkButton>
                    ) : (
                      <TertiaryButton
                        aria-expanded={isImagePickerOpen}
                        aria-invalid={!!imageError}
                        isDisabled={false}
                        trackingId={
                          SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_VARIATION_IMAGE_POPOVER
                        }
                      >
                        Change
                      </TertiaryButton>
                    )
                  }
                />
              )}
              {imageError && (
                <ErrorText>
                  <StyledIcon name='error' />
                  <CaptionErrorText>{imageError}</CaptionErrorText>
                </ErrorText>
              )}
            </ImageField>

            <FormFields>
              <FormField
                isViewOnly={isViewOnly}
                label='Name'
                name={`variations.${index}.variantName`}
                placeholder='Awesome Product Name'
                rules={rules[`variations.${index}.variantName`]}
              />

              <FormField
                inputType='text'
                instructions='Total products'
                isViewOnly={isViewOnly}
                label='Quantity'
                name={`variations.${index}.quantity`}
                placeholder='1,000'
                rules={{
                  ...rules[`variations.${index}.quantity`],
                  validate: (value: string | undefined) =>
                    !value?.trim() || validateQuantity(value),
                }}
                width='half'
              />

              <FormField
                inputType='text'
                instructions={currencyInstructions}
                isViewOnly={isViewOnly}
                label='Full Market Value (FMV)'
                name={`variations.${index}.fairMarketValue`}
                placeholder='25.99'
                rules={rules[`variations.${index}.fairMarketValue`]}
                width='half'
              />

              <FormField
                helpText={
                  !isViewOnly &&
                  !isBVShipped &&
                  !isGtinValid &&
                  isTruthy(gtinValue)
                    ? 'We highly recommend providing a valid GTIN, UPC, EAN, or PZN. A valid value can improve product identification and syndication matching.'
                    : ''
                }
                inputType='text'
                instructions='7-8 or 12-14 digits valid GTIN'
                isViewOnly={isViewOnly}
                label='Unique Product Code'
                name={`variations.${index}.logisticsRef`}
                placeholder='1309945678912'
                rules={{
                  ...rules[`variations.${index}.logisticsRef`],
                  validate: (value: string) => {
                    // For BV shipped products, enforce strict validation
                    // For non-BV shipped products, show warning but allow submission
                    if (!isBVShipped) {
                      return true;
                    }
                    // For BV shipped products, return the error message if validation failed
                    return validateProductIdentifier(value).reason ?? true;
                  },
                }}
                width='half'
              />
            </FormFields>
          </FieldsContainer>
        }
        headerAction={
          !isViewOnly && (
            <DefaultIconButton
              aria-label={`Remove variant ${index + 1}`}
              iconName='delete'
              isDisabled={index === 0 || totalVariations === 1}
              onClick={onRemove}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_REMOVE_VARIATION
              }
            />
          )
        }
        isExpanded={isExpanded}
        onToggleExpansion={onToggleExpansion}
        title={
          isTruthy(variantName) ? variantName : `Variant #${index + 1} Name`
        }
        trackingId={`${SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_VARIANTS_TAB}-accordion-${index + 1}`}
      />
    </Container>
  );
};

interface VariantAccordionProps {
  content: React.ReactNode;
  headerAction?: React.ReactNode;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  title: string;
  trackingId: string;
}

const VariantAccordion = ({
  content,
  headerAction,
  isExpanded,
  onToggleExpansion,
  title,
  trackingId,
}: VariantAccordionProps) => {
  const toggleId = useUniqueId('variant-accordion-toggle');
  const contentId = useUniqueId('variant-accordion-content');

  return (
    <AccordionWrapper>
      <AccordionHeader>
        <ToggleButton
          aria-controls={contentId}
          aria-expanded={isExpanded}
          id={toggleId}
          onClick={onToggleExpansion}
          trackingId={`${trackingId}-toggle`}
          type='button'
        >
          <ToggleIcon isExpanded={isExpanded} name='chevron-down' />
          <VisuallyHiddenText>
            {isExpanded ? 'Collapse' : 'Expand'} section
          </VisuallyHiddenText>
        </ToggleButton>
        <TitleLabel htmlFor={toggleId}>
          <SubsectionText ellipsize={1} title={title}>
            {title}
          </SubsectionText>
        </TitleLabel>
        {headerAction}
      </AccordionHeader>
      <AccordionContent
        aria-labelledby={toggleId}
        hidden={!isExpanded}
        id={contentId}
        role='region'
      >
        {content}
      </AccordionContent>
    </AccordionWrapper>
  );
};

const Container = styled.section`
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray2};
`;

const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.echo.spacing.xs};
  min-height: 48px;
  padding: ${({ theme }) => theme.echo.spacing.sm};
`;

const ToggleButton = styled(InvisibleButton)`
  flex: 0 0 auto;
  padding: 0;
`;

const TitleLabel = styled.label`
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  margin-left: ${({ theme }) => theme.echo.spacing.xs};
  cursor: pointer;

  /* Keep long variant names on a single line */
  > * {
    min-width: 0;
    width: 100%;
  }
`;

const ToggleIcon = styled(Icon)<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
    transform: rotate(${isExpanded ? '180deg' : '0deg'});
    transition: transform 200ms ease;
  `,
);

const AccordionContent = styled.section`
  padding-top: 0;
  padding-right: ${({ theme }) => theme.echo.spacing.sm};
  padding-bottom: ${({ theme }) => theme.echo.spacing.sm};
  padding-left: ${({ theme }) => theme.echo.spacing.sm};
`;

const FieldsContainer = styled.div`
  display: grid;
  grid-template-columns: 75px minmax(0, 1fr);
  gap: ${({ theme }) => theme.echo.spacing.xs};
`;

const ImageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.xs};
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.sm};
`;

const FieldLabel = styled(BodyText)`
  display: block;
`;

const ChangeImageLinkButton = styled(PrimaryLinkButton)`
  align-self: flex-start;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
